/**
 * FRONT-2550 round 3 — the stored Smart tagging run, as a tiny external store
 * over localStorage (per user), so it can be read synchronously in the first
 * render (no flash of an empty card) and written from the run mutation's own
 * `onSuccess` — which still fires after the card has unmounted, so leaving
 * Settings right after pressing the button keeps the run.
 *
 * Every storage access is guarded: storage can be absent or throw (private
 * window, blocked site data, node test runs). Without it the run still works
 * in memory for this page view.
 */

import { expireRun, parseStoredRun, runStorageKey, startRun, type StoredRun } from './smart-tagging-progress';

type Listener = () => void;
const listeners = new Set<Listener>();
// Per user: the raw string last parsed and its result, so `getSnapshot`
// returns a STABLE reference while storage is unchanged (required by
// useSyncExternalStore) and parse/expiry run once per change, not per render.
const cache = new Map<string, { raw: string | null; run: StoredRun | null }>();
// In-memory fallback when storage is unavailable.
const memory = new Map<string, string>();

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function readRaw(userId: string): string | null {
  try {
    const s = storage();
    if (s) return s.getItem(runStorageKey(userId));
  } catch {
    /* fall through to memory */
  }
  return memory.get(userId) ?? null;
}

function writeRaw(userId: string, raw: string | null): void {
  cache.delete(userId); // a write is a fresh load: parse and expiry run again
  try {
    const s = storage();
    if (s) {
      if (raw === null) s.removeItem(runStorageKey(userId));
      else s.setItem(runStorageKey(userId), raw);
      return;
    }
  } catch {
    /* fall through to memory */
  }
  if (raw === null) memory.delete(userId);
  else memory.set(userId, raw);
}

function emit(): void {
  for (const l of listeners) l();
}

/** The stored run for this user, with a finished run past its 24 h lifetime
 *  dropped (and removed from storage). Stable while storage is unchanged. */
export function getStoredRun(userId: string | undefined, now: number = Date.now()): StoredRun | null {
  if (!userId) return null;
  const raw = readRaw(userId);
  const hit = cache.get(userId);
  // A cache hit still honours the 24 h lifetime: a tab left open past it must
  // not keep showing yesterday's result. `expireRun` returns the same object
  // while the run is alive, so the reference stays stable.
  if (hit && hit.raw === raw && (hit.run === null || expireRun(hit.run, now) !== null)) return hit.run;
  let run = parseStoredRun(raw);
  if (run) run = expireRun(run, now);
  if (raw !== null && run === null) writeRaw(userId, null);
  cache.set(userId, { raw: run ? raw : null, run });
  return run;
}

export function saveStoredRun(userId: string, run: StoredRun): void {
  writeRaw(userId, JSON.stringify(run));
  emit();
}

export function clearStoredRun(userId: string): void {
  writeRaw(userId, null);
  emit();
}

/** What a successful press does: a run that queued nothing REPLACES (clears)
 *  the previous result rather than leaving yesterday's "Done" on screen. */
export function recordRunStart(userId: string, response: { startedAt: string; queued: number }): void {
  if (response.queued > 0) saveStoredRun(userId, startRun(response));
  else clearStoredRun(userId);
}

const RUN_KEY_PREFIX = runStorageKey('');

/** Another tab wrote (or cleared) a run: re-read. `storage` fires only in
 *  OTHER tabs, so same-tab writes still go through `emit()`. `key === null`
 *  is a `localStorage.clear()`. A changed raw string re-parses on its own
 *  (the cache is keyed by it), so emitting is all that is needed. */
function onStorageEvent(e: { key: string | null }): void {
  if (e.key === null || e.key.startsWith(RUN_KEY_PREFIX)) emit();
}

export function subscribeStoredRun(listener: Listener): () => void {
  const first = listeners.size === 0;
  listeners.add(listener);
  if (first) globalThis.addEventListener?.('storage', onStorageEvent);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) globalThis.removeEventListener?.('storage', onStorageEvent);
  };
}

/** Tests only. */
export function resetRunStoreForTests(): void {
  cache.clear();
  memory.clear();
  listeners.clear();
  globalThis.removeEventListener?.('storage', onStorageEvent);
}
